import { queryClient } from 'shared/queryClient';
import { clearJWT } from 'shared/services';
import {
  GTM,
  captureSentryException,
  clearSentryUser,
  clearPostHogUser,
  addSentryAuthBreadcrumb,
  isAuthPath
} from 'shared/utils';

let redirectStarted = false;

/**
 * True once a logout / session-expiry redirect has been initiated. The hard
 * `window.location.replace` is preceded by `queryClient.cancelQueries()`, whose
 * aborted in-flight requests reject as generic (status-500) errors; consumers
 * such as `GeneralError` read this to skip painting the error screen for that
 * teardown artifact while the redirect is still in flight.
 */
export const isSessionEndingRedirectInProgress = () => redirectStarted;

/**
 * sessionStorage key flagging an explicit logout, read once by the auth layout
 * to skip its cookie-based auto-login attempt.
 */
const EXPLICIT_LOGOUT_STORAGE_KEY = 'auth_explicit_logout';

/**
 * Flags the upcoming navigation as an explicit logout. The server-side token
 * revocation is fire-and-forget and can still be in flight (or aborted by the
 * navigation) when the login page loads — without the flag, the auth layout's
 * cookie auto-login could refresh the not-yet-revoked token and bounce the
 * user straight back into the app.
 */
const markExplicitLogout = () => {
  try {
    sessionStorage.setItem(EXPLICIT_LOGOUT_STORAGE_KEY, 'true');
  } catch {
    // Storage unavailable — worst case the auto-login attempt races the
    // revocation, matching pre-flag behaviour.
  }
};

/**
 * Returns true when the current page load follows an explicit logout, clearing
 * the flag so only one auto-login attempt is skipped.
 */
export const consumeExplicitLogout = () => {
  try {
    const isExplicitLogout = sessionStorage.getItem(EXPLICIT_LOGOUT_STORAGE_KEY) !== null;

    sessionStorage.removeItem(EXPLICIT_LOGOUT_STORAGE_KEY);

    return isExplicitLogout;
  } catch {
    return false;
  }
};

/**
 * Clears local session state (in-memory access token, query cache, analytics and
 * error-tracking user context) and hard-redirects. `replace` (not `href`) so the
 * current entry is overwritten — pressing Back from the destination must not
 * return the user to the authed New Platform.
 */
const clearSessionAndRedirect = async (redirectUrl: string) => {
  try {
    await queryClient.cancelQueries();
  } catch {
    // Non-critical — proceed with cleanup even if cancellation fails
  }
  GTM.clearAuthData();
  clearSentryUser();
  clearPostHogUser();
  clearJWT();
  queryClient.clear();
  window.location.replace(redirectUrl);
};

/**
 * Handles session expiry signalled by the auth middleware (`onSessionExpired`):
 * clears local state and redirects to the session-expired login URL.
 *
 * No-ops when already on an unauthenticated /auth route: those routes revalidate
 * the httpOnly cookie themselves (e.g. sign-up's `beforeLoad` calls `ensureAuthMe`
 * on every load) and handle the resulting 401 in place — redirecting would show a
 * false "Session expired" toast to anonymous visitors and could reload the page
 * in a loop. Deliberately does not latch `redirectStarted` in that case, so a
 * later expiry after signing back in still redirects.
 *
 * Shares the one-shot redirect latch with `logout` so a racing session-expiry
 * event cannot override the logout redirect target (or vice versa).
 */
export const handleSessionExpired = async (redirectUrl: string) => {
  if (redirectStarted) return;
  if (isAuthPath(window.location.pathname)) return;

  redirectStarted = true;

  addSentryAuthBreadcrumb('handleSessionExpired', 'logout', { redirectUrl });

  await clearSessionAndRedirect(redirectUrl);
};

/**
 * Full logout — revokes the server-side session, then clears local session state
 * and hard-redirects. Use only for explicit user-initiated logout; session expiry
 * is handled separately by `handleSessionExpired`.
 *
 * Revocation is awaited before the redirect: `window.location.replace` tears
 * down the document, which can abort the in-flight revoke and leave the session
 * alive — silently signing the user back in on the next load (CE-4053).
 * Deliberately no client-side timeout; a hung endpoint blocks the redirect.
 *
 * The redirect is guarded by the one-shot latch shared with `handleSessionExpired`,
 * so a racing session-expiry event cannot override the logout redirect target
 * (or vice versa).
 *
 * `apiClient` is imported lazily to avoid a circular dependency with
 * `shared/apiClient`, which wires `handleSessionExpired` at module init time.
 */
export const logout = async (redirectUrl: string) => {
  markExplicitLogout();

  // Acquire the shared latch before the revoke: a session expiry racing the
  // in-flight revoke must not win and override the logout redirect target.
  if (redirectStarted) return;
  redirectStarted = true;

  addSentryAuthBreadcrumb('logout', 'logout', { redirectUrl });

  try {
    const { apiClient } = await import('shared/apiClient');

    await apiClient.auth.logout();
  } catch (error) {
    captureSentryException({
      message: 'Failed to revoke refresh token server-side during logout.',
      feature: 'shared',
      error
    });
  }

  await clearSessionAndRedirect(redirectUrl);
};
